import { useCallback } from 'react'
import { posthog, isPostHogEnabled } from './posthog'
import type { EventPayloadMap } from './events'
import type { EVENTS } from './events'

type EventName = (typeof EVENTS)[keyof typeof EVENTS]

/**
 * Hook for tracking analytics events in React components.
 * Returns a stable `track` function that no-ops when PostHog is disabled.
 */
export function useAnalytics() {
  const track = useCallback(
    <E extends EventName>(
      event: E,
      properties?: E extends keyof EventPayloadMap ? EventPayloadMap[E] : Record<string, unknown>,
    ) => {
      if (!isPostHogEnabled()) return
      posthog.capture(event, properties)
    },
    [],
  )

  const startSessionRecording = useCallback(() => {
    if (!isPostHogEnabled()) return
    posthog.startSessionRecording()
  }, [])

  const stopSessionRecording = useCallback(() => {
    if (!isPostHogEnabled()) return
    posthog.stopSessionRecording()
  }, [])

  return { track, startSessionRecording, stopSessionRecording }
}

/**
 * Imperative track function for use outside React components (e.g. Zustand stores).
 * No-ops when PostHog is disabled.
 */
export function trackEvent<E extends EventName>(
  event: E,
  properties?: E extends keyof EventPayloadMap ? EventPayloadMap[E] : Record<string, unknown>,
) {
  if (!isPostHogEnabled()) return
  posthog.capture(event, properties)
}

/**
 * Report a caught error to PostHog error tracking with structured context.
 * No-ops when PostHog is disabled. Deliberately does NOT pre-filter: the
 * deny list, dedup and session cap all live in `before_send`
 * (exception-filter.ts), which sees this and autocaptured errors alike.
 * Filtering here too would dedup the event against itself and drop it.
 */
export function captureException(error: unknown, properties?: Record<string, unknown>) {
  if (!isPostHogEnabled()) return
  posthog.captureException(error, properties)
}
